import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '@/pages/home/components/Footer';
import type { SeoArticle } from '@/mocks/seo-articles';

declare const __VITE_SITE_URL__: string;

function generateKeywords(title: string, description: string): string {
  const words = [...title.split(/\s+/), ...description.split(/\s+/)];
  const stopWords = new Set(['the','a','an','is','in','on','to','for','of','and','or','with','our','your','that','this','from','each','all','are','be','how','what','why','not','no','you','we','it','its','at','by']);
  const filtered = words
    .map(w => w.replace(/[^a-zA-Z0-9]/g, '').toLowerCase())
    .filter(w => w.length > 3 && !stopWords.has(w));
  const unique = [...new Set(filtered)];
  return unique.slice(0, 8).join(', ');
}

export default function SeoArticleLayout({ article }: { article: SeoArticle }) {
  const siteUrl = __VITE_SITE_URL__;
  const canonicalUrl = `${siteUrl}${article.slug}`;
  const keywords = generateKeywords(article.title, article.description);
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image: article.imageSrc,
    url: canonicalUrl,
    datePublished: '2026-06-15',
    dateModified: '2026-07-01',
    author: { '@type': 'Organization', name: 'pandabuy.bar' },
    publisher: { '@type': 'Organization', name: 'pandabuy.bar', url: siteUrl },
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <title>{article.title} | pandabuy.bar</title>
      <meta name="description" content={article.description} />
      <meta name="keywords" content={`pandabuy spreadsheet, ${keywords}`} />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:title" content={`${article.title} | pandabuy.bar`} />
      <meta property="og:description" content={article.description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="article" />
      <meta property="og:image" content={article.imageSrc} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${article.title} | pandabuy.bar`} />
      <meta name="twitter:description" content={article.description} />
      <meta name="twitter:image" content={article.imageSrc} />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200">
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <Link
              to="/"
              className="font-heading text-xl font-bold text-foreground-950 tracking-tight whitespace-nowrap"
            >
              pandabuy<span className="text-primary-500">.</span>bar
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 bg-primary-500 text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-sm"></i>
              </span>
            </a>
          </div>
        </div>
      </nav>

      <main>
        {/* Header */}
        <section className="pt-24 pb-12 md:pt-28 md:pb-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-background-300 text-sm text-foreground-600 hover:text-foreground-950 hover:border-foreground-300 transition-colors whitespace-nowrap cursor-pointer mb-8"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </span>
              Back to Hub
            </Link>

            <div className="flex flex-col lg:flex-row items-start gap-10 lg:gap-16">
              <div className="flex-1 w-full">
                <span className="inline-block px-3 py-1 rounded-full bg-accent-50 text-accent-700 text-xs font-semibold uppercase tracking-wider mb-4">
                  SEO Article
                </span>
                <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight">
                  {article.title}
                </h1>
                <p className="mt-5 text-base text-foreground-600 leading-relaxed max-w-xl">
                  {article.description}
                </p>
                <div className="flex items-center gap-6 mt-6 text-xs text-foreground-400">
                  <span>Updated: June 2026</span>
                  <span>{article.readTime}</span>
                  <span>By pandabuy.bar Team</span>
                </div>
              </div>

              <div className="flex-1 w-full flex gap-4">
                <div className="flex-1 rounded-2xl overflow-hidden" style={{ aspectRatio: '3/4', maxHeight: '400px' }}>
                  <img
                    src={article.imageSrc}
                    alt={article.imageAlt}
                    className="w-full h-full object-cover object-top"
                    title={article.title}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Body */}
        <article className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto">
            {article.sections.map((section, sIdx) => (
              <section key={sIdx} className="mb-14">
                {section.heading && (
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                    {section.heading}
                  </h2>
                )}
                {section.isOrdered ? (
                  <ol className="list-decimal list-inside space-y-4 text-base text-foreground-600 leading-relaxed">
                    {section.paragraphs.map((p, pIdx) => (
                      <li key={pIdx}>{p}</li>
                    ))}
                  </ol>
                ) : (
                  section.paragraphs.map((p, pIdx) => (
                    <p key={pIdx} className="text-base text-foreground-600 leading-relaxed mb-4">
                      {p}
                    </p>
                  ))
                )}
              </section>
            ))}

            {/* Final CTA */}
            <section className="text-center bg-background-100 rounded-2xl p-8 md:p-12 border border-background-200">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                Explore the Full PandabuySpreadsheet
              </h2>
              <p className="text-foreground-600 text-base mb-6 max-w-md mx-auto">
                Return to the hub and browse all 14 curated categories — or jump straight to the main store for the complete catalog.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 bg-primary-500 text-white px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Browse Categories
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line"></i>
                  </span>
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 bg-foreground-950 text-white px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Visit Main Store
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-up-line"></i>
                  </span>
                </a>
              </div>
            </section>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}